// Company.styles.ts
import styled, { keyframes } from "styled-components";

// ===== ANIMAÇÕES =====
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// ===== CONTAINER PRINCIPAL =====
export const FieldsContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: 85%;
  max-height: 85%;
  padding: var(--sp-24);
  flex-direction: column;
  box-sizing: border-box;
  overflow: auto;

  &::-webkit-scrollbar {
    width: calc(var(--u) * 15);
    height: calc(var(--u) * 15);
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors.modalColor + "90"};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.colors.selection};
    border-radius: 10px;
    border: 2px solid ${(props) => props.theme.colors.modalColor + "90"};
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${(props) => props.theme.colors.selectionDark};
    cursor: grab;
  }

  &::-webkit-scrollbar-thumb:active {
    cursor: grabbing;
  }

  &:hover {
    scrollbar-width: auto;
  }
  scrollbar-color: ${(props) => props.theme.colors.selection};
`;

// ===== CABEÇALHO =====
export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--sp-32);
  flex-wrap: wrap;
  gap: var(--sp-20);
  animation: ${fadeIn} 0.4s ease;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-8);
`;

export const HeaderTitle = styled.h1`
  font-size: var(--fs-4xl);
  font-weight: 700;
  margin: 0;
  color: ${(props) => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: var(--sp-12);
`;

export const HeaderSubtitle = styled.p`
  color: ${(props) => props.theme.colors.label};
  margin: 0;
  font-size: var(--fs-base);
`;

// ===== FILTROS =====
export const FilterSection = styled.div`
  display: flex;
  gap: var(--sp-16);
  margin-bottom: var(--sp-24);
  flex-wrap: wrap;
  align-items: center;
  animation: ${slideIn} 0.4s ease;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const SearchWrapper = styled.div`
  flex: 1;
  min-width: calc(var(--u) * 300);
  position: relative;
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

export const SearchIcon = styled.div`
  position: absolute;
  left: calc(var(--u) * 12);
  color: ${(props) => props.theme.colors.label};
  pointer-events: none;
  display: flex;
  align-items: center;
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: var(--sp-12) calc(var(--u) * 44) var(--sp-12) calc(var(--u) * 44);
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  font-size: var(--fs-md);
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.text};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.selection};
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.selection + "20"};
  }

  &::placeholder {
    color: ${(props) => props.theme.colors.label};
  }
`;

export const ClearButton = styled.button`
  position: absolute;
  right: calc(var(--u) * 8);
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.label};
  cursor: pointer;
  padding: var(--sp-4);
  display: flex;
  align-items: center;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) => props.theme.colors.border};
    color: ${(props) => props.theme.colors.text};
  }
`;

export const FilterSelect = styled.select`
  padding: var(--sp-12) var(--sp-16);
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  font-size: var(--fs-md);
  min-width: calc(var(--u) * 150);
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.text};
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.selection};
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.selection + "20"};
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

// ===== ESTATÍSTICAS =====
export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(calc(var(--u) * 200), 1fr));
  gap: var(--sp-16);
  margin-bottom: var(--sp-32);
  animation: ${scaleIn} 0.5s ease;
`;

export const StatCard = styled.div<{ $color: string }>`
  padding: var(--sp-20);
  background: linear-gradient(
    135deg,
    ${(props) => props.$color + "15"} 0%,
    ${(props) => props.$color + "08"} 100%
  );
  border: 2px solid ${(props) => props.$color + "30"};
  border-radius: 12px;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px ${(props) => props.$color + "20"};
    border-color: ${(props) => props.$color + "50"};
  }
`;

export const StatIcon = styled.div`
  font-size: var(--fs-4xl);
  margin-bottom: var(--sp-8);
`;

export const StatValue = styled.div`
  font-size: var(--fs-4xl);
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: var(--sp-4);
`;

export const StatLabel = styled.div`
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
  font-weight: 600;
`;

// ===== LOADING =====
export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: calc(var(--u) * 60) var(--sp-20);
  gap: var(--sp-16);
  color: ${(props) => props.theme.colors.label};
  font-size: var(--fs-base);
  animation: ${fadeIn} 0.3s ease;
`;

export const LoadingSpinner = styled.div`
  width: calc(var(--u) * 48);
  height: calc(var(--u) * 48);
  border: 4px solid ${(props) => props.theme.colors.border};
  border-top-color: ${(props) => props.theme.colors.selection};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

// ===== LISTA DE ESTABELECIMENTOS =====
export const EstablishmentsList = styled.div`
  display: grid;
  gap: var(--sp-20);
  animation: ${slideIn} 0.4s ease;
`;

export const EstablishmentCard = styled.div`
  background-color: ${(props) => props.theme.colors.background};
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: 12px;
  padding: var(--sp-24);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    border-color: ${(props) => props.theme.colors.selection};
  }
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--sp-16);
  gap: var(--sp-20);

  @media (max-width: 968px) {
    flex-direction: column;
  }
`;

export const CardHeaderLeft = styled.div`
  display: flex;
  gap: var(--sp-16);
  flex: 1;
  align-items: flex-start;

  @media (max-width: 568px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

export const CompanyLogo = styled.img`
  width: calc(var(--u) * 80);
  height: calc(var(--u) * 80);
  border-radius: 12px;
  object-fit: cover;
  border: 2px solid ${(props) => props.theme.colors.border};
  flex-shrink: 0;
`;

export const CompanyLogoPlaceholder = styled.div`
  width: calc(var(--u) * 80);
  height: calc(var(--u) * 80);
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    ${(props) => props.theme.colors.selection + "20"} 0%,
    ${(props) => props.theme.colors.selection + "10"} 100%
  );
  border: 2px dashed ${(props) => props.theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.colors.label};
  flex-shrink: 0;
`;

export const CardInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-12);
  margin-bottom: var(--sp-8);
  flex-wrap: wrap;
`;

export const CardTitle = styled.h3`
  margin: 0;
  font-size: var(--fs-xl);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
`;

export const StatusBadge = styled.span<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-4);
  padding: var(--sp-4) var(--sp-12);
  border-radius: 20px;
  font-size: var(--fs-xs);
  font-weight: 600;
  background-color: ${(props) => (props.$isActive ? "#d4edda" : "#f8d7da")};
  color: ${(props) => (props.$isActive ? "#155724" : "#721c24")};
  border: 1px solid ${(props) => (props.$isActive ? "#c3e6cb" : "#f5c6cb")};
`;

export const SubsidiaryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-4);
  padding: var(--sp-4) var(--sp-10);
  border-radius: 12px;
  font-size: var(--fs-2xs);
  font-weight: 600;
  background-color: ${(props) => props.theme.colors.selection + "20"};
  color: ${(props) => props.theme.colors.selection};
  border: 1px solid ${(props) => props.theme.colors.selection + "40"};
`;

export const CardDescription = styled.p`
  margin: 0 0 var(--sp-12) 0;
  color: ${(props) => props.theme.colors.label};
  line-height: 1.5;
  font-size: var(--fs-md);
`;

export const CardMeta = styled.div`
  display: flex;
  gap: var(--sp-16);
  flex-wrap: wrap;
`;

export const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: var(--sp-6);
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};

  strong {
    font-weight: 600;
    color: ${(props) => props.theme.colors.text};
  }
`;

export const CardActions = styled.div`
  display: flex;
  gap: var(--sp-8);
  flex-shrink: 0;

  @media (max-width: 968px) {
    width: 100%;
    justify-content: flex-end;
  }

  @media (max-width: 568px) {
    flex-direction: column;
  }
`;

// ===== SEÇÃO DE SUBSIDIÁRIAS =====
export const SubsidiariesSection = styled.div`
  border-top: 1px solid ${(props) => props.theme.colors.border};
  padding-top: var(--sp-16);
  margin-top: var(--sp-16);
`;

export const SubsidiariesHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  margin-bottom: var(--sp-12);
  font-weight: 600;
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
`;

export const SubsidiariesList = styled.div`
  display: flex;
  gap: var(--sp-8);
  flex-wrap: wrap;
`;

export const SubsidiaryTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-6);
  padding: var(--sp-6) var(--sp-12);
  background-color: ${(props) => props.theme.colors.background + "80"};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 20px;
  font-size: var(--fs-xs);
  color: ${(props) => props.theme.colors.text};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) => props.theme.colors.selection + "10"};
    border-color: ${(props) => props.theme.colors.selection};
  }
`;

// ===== ESTADO VAZIO =====
export const EmptyState = styled.div`
  background-color: ${(props) => props.theme.colors.background};
  border: 2px dashed ${(props) => props.theme.colors.border};
  border-radius: 12px;
  padding: calc(var(--u) * 60) var(--sp-40);
  text-align: center;
  animation: ${fadeIn} 0.4s ease;
`;

export const EmptyStateIcon = styled.div`
  font-size: var(--fs-7xl);
  margin-bottom: var(--sp-16);
  animation: ${pulse} 2s ease-in-out infinite;
`;

export const EmptyStateTitle = styled.div`
  font-size: var(--fs-xl);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: var(--sp-8);
`;

export const EmptyStateDescription = styled.div`
  font-size: var(--fs-base);
  color: ${(props) => props.theme.colors.label};
  margin-bottom: var(--sp-16);
`;

// ===== CONTADOR DE RESULTADOS =====
export const ResultsCounter = styled.div`
  margin-top: var(--sp-24);
  padding: var(--sp-12);
  text-align: center;
  color: ${(props) => props.theme.colors.label};
  font-size: var(--fs-md);
  background-color: ${(props) => props.theme.colors.background + "60"};
  border-radius: 8px;
  animation: ${fadeIn} 0.3s ease;

  strong {
    color: ${(props) => props.theme.colors.text};
    font-weight: 600;
  }
`;

// ===== BOTÕES =====
export const Button = styled.button<{ color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-10) var(--sp-20);
  border-radius: 8px;
  border: none;
  background-color: ${(props) => props.color || props.theme.colors.selection};
  color: ${(props) => props.theme.colors.textOnPrimary};
  font-weight: 600;
  font-size: var(--fs-md);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  gap: var(--sp-6);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${(props) => props.theme.colors.selection + "40"};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    transform: none;
  }

  @media (max-width: 568px) {
    width: 100%;
  }
`;

export const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-10) var(--sp-20);
  background-color: transparent;
  color: ${(props) => props.theme.colors.text};
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  cursor: pointer;
  font-size: var(--fs-md);
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  gap: var(--sp-6);

  &:hover:not(:disabled) {
    background-color: ${(props) => props.theme.colors.background};
    border-color: ${(props) => props.theme.colors.text + "40"};
    transform: translateY(-2px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  @media (max-width: 568px) {
    width: 100%;
  }
`;

// ===== ÍCONES =====
export const MaterialIcons = styled.span`
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  font-size: var(--fs-xl);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

// ===== EXPORTAÇÃO PADRÃO =====
export default {
  FieldsContainer,
  Header,
  HeaderContent,
  HeaderTitle,
  HeaderSubtitle,
  FilterSection,
  SearchWrapper,
  SearchIcon,
  SearchInput,
  ClearButton,
  FilterSelect,
  StatsGrid,
  StatCard,
  StatIcon,
  StatValue,
  StatLabel,
  LoadingContainer,
  LoadingSpinner,
  EstablishmentsList,
  EstablishmentCard,
  CardHeader,
  CardHeaderLeft,
  CompanyLogo,
  CompanyLogoPlaceholder,
  CardInfo,
  CardTitleRow,
  CardTitle,
  StatusBadge,
  SubsidiaryBadge,
  CardDescription,
  CardMeta,
  MetaItem,
  CardActions,
  SubsidiariesSection,
  SubsidiariesHeader,
  SubsidiariesList,
  SubsidiaryTag,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
  ResultsCounter,
  Button,
  SecondaryButton,
  MaterialIcons,
};
